// Pagina PROFIT TASKS - Task con lato economico (pipeline commerciale)
import { useEffect, useState, useMemo } from 'react';
import { Header } from '@/components/layout/Header';
import { PageContainer, KanbanPageContainer } from '@/components/layout/Layout';
import { Button } from '@/components/ui/Button';
import { Input, Textarea, Select, Field } from '@/components/ui/Input';
import { NumberInput } from '@/components/ui/NumberInput';
import { Modal } from '@/components/ui/Modal';
import { EmptyState, Badge } from '@/components/ui/Misc';
import { Card } from '@/components/ui/Card';
import { KanbanBoard } from '@/components/kanban/KanbanBoard';
import { useProfitTasksStore } from '@/store/profitTasksStore';
import { useSettingsStore } from '@/store/settingsStore';
import { toast } from '@/hooks/useToast';
import { confirmDialog } from '@/components/ui/Confirm';
import { useI18n } from '@/i18n';
import { PROFIT_COLUMNS, PRIORITY_META, PRIORITIES, PROFIT_STATUS_META, CURRENCIES } from '@/utils/constants';
import { cn, formatCurrency, formatDate, daysUntil, toDatetimeLocalValue, fromDatetimeLocalValue, nowISO } from '@/utils';
import type { ProfitTask, ProfitStatus, Priority } from '@/types';
import { TrendingUp, Plus, Trash2, Calendar, Flag, User, Wallet, ChevronDown, ChevronUp } from 'lucide-react';

export function ProfitTasksPage() {
  const { t } = useI18n();
  const { tasks, load, create, update, remove, move, reorder } = useProfitTasksStore();
  const { settings } = useSettingsStore();
  const [editing, setEditing] = useState<ProfitTask | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createStatus, setCreateStatus] = useState<ProfitStatus>('lead');
  const [showStats, setShowStats] = useState(false);

  useEffect(() => {
    load();
  }, [load]);

  // Statistiche aggregate
  const stats = useMemo(() => {
    const active = tasks.filter((ts) => ts.status !== 'cancelled');
    const paid = tasks.filter((ts) => ts.status === 'paid');
    const expectedRevenue = active.reduce((s, ts) => s + (ts.expected_revenue || 0), 0);
    const actualRevenue = paid.reduce((s, ts) => s + (ts.actual_revenue || 0), 0);
    const totalCost = active.reduce((s, ts) => s + (ts.cost || 0), 0);
    const paidCost = paid.reduce((s, ts) => s + (ts.cost || 0), 0);
    return {
      expectedRevenue,
      actualRevenue,
      totalCost,
      expectedMargin: expectedRevenue - totalCost,
      actualMargin: actualRevenue - paidCost,
      pipeline: active.filter((ts) => ts.status !== 'paid').reduce((s, ts) => s + (ts.expected_revenue || 0), 0),
    };
  }, [tasks]);

  function handleAdd(status: ProfitStatus) {
    setCreateStatus(status);
    setShowCreate(true);
  }

  // Kolonne tradotte
  const columns = PROFIT_COLUMNS.map((c) => ({
    ...c,
    title: t(`profit.status_${c.status}`),
  }));

  return (
    <>
      <Header
        title={t('profit.title')}
        subtitle={t('profit.subtitle', {
          active: tasks.filter((ts) => ts.status !== 'paid' && ts.status !== 'cancelled').length,
          revenue: formatCurrency(stats.actualRevenue, settings.default_currency),
        })}
        icon={<div className="w-9 h-9 rounded-xl bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center"><TrendingUp size={18} className="text-emerald-600 dark:text-emerald-400" /></div>}
        actions={
          <Button size="sm" onClick={() => { setCreateStatus('lead'); setShowCreate(true); }}>
            <Plus size={16} /> {t('common.new')}
          </Button>
        }
      />

      <KanbanPageContainer className="!py-3">
        {/* Stats card collapsibile */}
        <div className="px-4 mb-3 shrink-0">
          <Card className="overflow-hidden">
            <button
              onClick={() => setShowStats((v) => !v)}
              className="w-full flex items-center justify-between p-3"
            >
              <div className="flex items-center gap-2">
                <Wallet size={16} className="text-emerald-500" />
                <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">{t('profit.stats_title')}</span>
              </div>
              {showStats ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
            </button>
            {showStats && (
              <div className="grid grid-cols-2 gap-px bg-slate-100 dark:bg-slate-800 border-t border-slate-100 dark:border-slate-800">
                <StatCell label={t('profit.stats_pipeline')} value={formatCurrency(stats.pipeline, settings.default_currency)} color="text-blue-600 dark:text-blue-400" />
                <StatCell label={t('profit.stats_expected')} value={formatCurrency(stats.expectedRevenue, settings.default_currency)} color="text-indigo-600 dark:text-indigo-400" />
                <StatCell label={t('profit.stats_actual')} value={formatCurrency(stats.actualRevenue, settings.default_currency)} color="text-emerald-600 dark:text-emerald-400" />
                <StatCell label={t('profit.stats_cost')} value={formatCurrency(stats.totalCost, settings.default_currency)} color="text-rose-600 dark:text-rose-400" />
                <StatCell label={t('profit.stats_margin_expected')} value={formatCurrency(stats.expectedMargin, settings.default_currency)} color="text-emerald-600 dark:text-emerald-400" />
                <StatCell label={t('profit.stats_margin_real')} value={formatCurrency(stats.actualMargin, settings.default_currency)} color="text-emerald-600 dark:text-emerald-400" />
              </div>
            )}
          </Card>
        </div>

        {tasks.length === 0 ? (
          <div className="overflow-y-auto flex-1">
            <EmptyState
              icon={<TrendingUp size={48} strokeWidth={1.5} />}
              title={t('profit.empty_title')}
              description={t('profit.empty_desc')}
              action={<Button onClick={() => setShowCreate(true)}><Plus size={16} /> {t('common.new')}</Button>}
            />
          </div>
        ) : (
          <KanbanBoard<ProfitTask>
            columns={columns}
            items={tasks}
            onMove={(id, status, position) => move(id, status as ProfitStatus, position)}
            onReorder={(status, ids) => reorder(status as ProfitStatus, ids)}
            onEdit={(item) => setEditing(item)}
            onAdd={handleAdd}
            renderCard={(item) => <ProfitCard item={item} currency={settings.default_currency} />}
          />
        )}
      </KanbanPageContainer>

      {showCreate && (
        <ProfitEditor
          initialStatus={createStatus}
          defaultCurrency={settings.default_currency}
          onClose={() => setShowCreate(false)}
          onSave={async (data) => {
            await create(data);
            toast.success(t('profit.created'));
            setShowCreate(false);
          }}
        />
      )}

      {editing && (
        <ProfitEditor
          task={editing}
          defaultCurrency={settings.default_currency}
          onClose={() => setEditing(null)}
          onSave={async (data) => {
            await update(editing.id, data);
            toast.success(t('profit.updated'));
            setEditing(null);
          }}
          onDelete={async () => {
            confirmDialog({
              title: t('profit.delete_confirm_title'),
              message: t('profit.delete_confirm_msg'),
              confirmText: t('common.delete'),
              danger: true,
              onConfirm: async () => {
                await remove(editing.id);
                toast.success(t('profit.deleted'));
                setEditing(null);
              },
            });
          }}
        />
      )}
    </>
  );
}

// ------------------------------------------------------------
// Stat cell
// ------------------------------------------------------------
function StatCell({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div className="bg-white dark:bg-slate-900 p-3">
      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">{label}</p>
      <p className={cn('text-base font-bold mt-0.5', color)}>{value}</p>
    </div>
  );
}

// ------------------------------------------------------------
// Card
// ------------------------------------------------------------
function ProfitCard({ item, currency }: { item: ProfitTask; currency: string }) {
  const { t } = useI18n();
  const priority = PRIORITY_META[item.priority];
  const days = daysUntil(item.due_date);
  const overdue = days !== null && days < 0 && item.status !== 'paid' && item.status !== 'cancelled';
  const soon = days !== null && days >= 0 && days <= 3 && item.status !== 'paid' && item.status !== 'cancelled';
  const margin = item.expected_revenue - item.cost;
  const marginPct = item.expected_revenue > 0 ? (margin / item.expected_revenue) * 100 : 0;
  const isPaid = item.status === 'paid';

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700/60 p-3 shadow-sm hover:shadow-md transition-shadow cursor-grab active:cursor-grabbing">
      <div className="flex items-start gap-2 mb-1.5">
        <span className={cn('shrink-0 w-1.5 h-1.5 rounded-full mt-1.5', priority.dot)} />
        <h4 className="text-sm font-medium text-slate-900 dark:text-white flex-1 line-clamp-2">{item.title || t('notes.untitled')}</h4>
      </div>

      {item.client_name && (
        <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1 mb-1.5">
          <User size={11} /> {item.client_name}
        </p>
      )}

      {item.description && (
        <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 mb-2">{item.description}</p>
      )}

      {/* Economic block */}
      <div className="rounded-lg bg-slate-50 dark:bg-slate-900/60 p-2 mb-2 space-y-1">
        <div className="flex items-center justify-between text-xs">
          <span className="text-slate-400 flex items-center gap-1"><Wallet size={10} /> {t('profit.field_expected')}</span>
          <span className="font-semibold text-slate-700 dark:text-slate-200">
            {formatCurrency(isPaid ? item.actual_revenue : item.expected_revenue, item.currency || currency)}
          </span>
        </div>
        {item.cost > 0 && (
          <div className="flex items-center justify-between text-xs">
            <span className="text-slate-400">{t('profit.field_cost')}</span>
            <span className="font-medium text-rose-500">-{formatCurrency(item.cost, item.currency || currency)}</span>
          </div>
        )}
        <div className="flex items-center justify-between text-xs pt-1 border-t border-slate-200 dark:border-slate-700">
          <span className="text-slate-400">{t('profit.margin_expected')}</span>
          <span className={cn('font-bold', margin >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-500')}>
            {formatCurrency(margin, item.currency || currency)} ({marginPct.toFixed(0)}%)
          </span>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <Badge className={cn('text-[10px]', priority.color)}>
          <Flag size={9} /> {t(`personal.priority_${item.priority}`)}
        </Badge>
        {item.due_date && (
          <Badge className={cn('text-[10px]', overdue ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300' : soon ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300')}>
            <Calendar size={9} /> {formatDate(item.due_date)}
          </Badge>
        )}
      </div>

      {overdue && <p className="text-[10px] text-rose-500 font-medium mt-1.5">{t('personal.overdue', { days: Math.abs(days!) })}</p>}
      {soon && <p className="text-[10px] text-amber-500 font-medium mt-1.5">{t('personal.due_soon', { days: days! })}</p>}
    </div>
  );
}

// ------------------------------------------------------------
// Editor
// ------------------------------------------------------------
function ProfitEditor({
  task,
  initialStatus,
  defaultCurrency,
  onClose,
  onSave,
  onDelete,
}: {
  task?: ProfitTask;
  initialStatus?: ProfitStatus;
  defaultCurrency: string;
  onClose: () => void;
  onSave: (data: Partial<ProfitTask>) => Promise<void>;
  onDelete?: () => Promise<void>;
}) {
  const { t } = useI18n();
  const [title, setTitle] = useState(task?.title ?? '');
  const [description, setDescription] = useState(task?.description ?? '');
  const [status, setStatus] = useState<ProfitStatus>(task?.status ?? initialStatus ?? 'lead');
  const [priority, setPriority] = useState<Priority>(task?.priority ?? 'medium');
  const [clientName, setClientName] = useState(task?.client_name ?? '');
  const [expectedRevenue, setExpectedRevenue] = useState<string>(
    task && task.expected_revenue ? String(task.expected_revenue) : ''
  );
  const [actualRevenue, setActualRevenue] = useState<string>(
    task && task.actual_revenue ? String(task.actual_revenue) : ''
  );
  const [cost, setCost] = useState<string>(
    task && task.cost ? String(task.cost) : ''
  );
  const [currency, setCurrency] = useState(task?.currency ?? defaultCurrency);
  const [dueDate, setDueDate] = useState(task?.due_date ?? null);
  const [tagsInput, setTagsInput] = useState(task?.tags.join(', ') ?? '');
  const [saving, setSaving] = useState(false);

  const expNum = Number(expectedRevenue) || 0;
  const costNum = Number(cost) || 0;
  const actNum = Number(actualRevenue) || 0;
  const margin = expNum - costNum;
  const marginPct = expNum > 0 ? (margin / expNum) * 100 : 0;

  async function handleSave() {
    setSaving(true);
    try {
      const tags = tagsInput.split(',').map((tg) => tg.trim().replace(/^#/, '')).filter(Boolean);
      await onSave({
        title, description, status, priority, client_name: clientName,
        expected_revenue: expNum, actual_revenue: actNum, cost: costNum,
        currency, due_date: dueDate, tags,
        updated_at: nowISO(),
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={task ? t('profit.editor_edit') : t('profit.editor_new')}
      size="lg"
      footer={
        <div className="flex justify-between items-center">
          {onDelete && task ? (
            <Button variant="danger" size="sm" onClick={onDelete}>
              <Trash2 size={14} /> {t('common.delete')}
            </Button>
          ) : <div />}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
            <Button loading={saving} onClick={handleSave} disabled={!title.trim()}>{t('common.save')}</Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label={t('profit.field_title')}>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('profit.field_title_ph')} autoFocus />
        </Field>

        <Field label={t('profit.field_desc')}>
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t('profit.field_desc_ph')} rows={2} />
        </Field>

        <Field label={t('profit.field_client')}>
          <Input value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder={t('profit.field_client_ph')} />
        </Field>

        {/* Section economica */}
        <div className="rounded-xl bg-emerald-50/50 dark:bg-emerald-900/10 border border-emerald-100 dark:border-emerald-900/30 p-3 space-y-3">
          <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
            <Wallet size={16} />
            <span className="text-xs font-semibold uppercase tracking-wider">{t('profit.economic_data')}</span>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t('profit.field_expected')}>
              <NumberInput value={expectedRevenue} onValueChange={setExpectedRevenue} placeholder="0,00" />
            </Field>
            <Field label={t('profit.field_actual')}>
              <NumberInput value={actualRevenue} onValueChange={setActualRevenue} placeholder="0,00" />
            </Field>
            <Field label={t('profit.field_cost')}>
              <NumberInput value={cost} onValueChange={setCost} placeholder="0,00" />
            </Field>
            <Field label={t('profit.field_currency')}>
              <Select value={currency} onChange={(e) => setCurrency(e.target.value)}>
                {CURRENCIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.label}</option>
                ))}
              </Select>
            </Field>
          </div>

          <div className="flex items-center justify-between text-sm bg-white dark:bg-slate-900 rounded-lg px-3 py-2">
            <span className="text-slate-500 dark:text-slate-400">{t('profit.margin_expected')}</span>
            <span className={cn('font-bold', margin >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-500')}>
              {formatCurrency(margin, currency)} ({marginPct.toFixed(1)}%)
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t('profit.field_status')}>
            <Select value={status} onChange={(e) => setStatus(e.target.value as ProfitStatus)}>
              {(Object.keys(PROFIT_STATUS_META) as ProfitStatus[]).map((s) => (
                <option key={s} value={s}>{t(`profit.status_${s}`)}</option>
              ))}
            </Select>
          </Field>

          <Field label={t('profit.field_priority')}>
            <Select value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>{t(`personal.priority_${p}`)}</option>
              ))}
            </Select>
          </Field>
        </div>

        <Field label={t('profit.field_due')}>
          <Input
            type="datetime-local"
            value={toDatetimeLocalValue(dueDate)}
            onChange={(e) => setDueDate(fromDatetimeLocalValue(e.target.value))}
          />
        </Field>

        <Field label={t('profit.field_tags')}>
          <Input value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} placeholder={t('notes.field_tags_ph')} />
        </Field>
      </div>
    </Modal>
  );
}
